import React, { useImperativeHandle, useRef, useState } from 'react';
import { Alert, Button, message, Modal, Space, Spin } from 'antd';
import intl from 'react-intl-universal';
import moment from 'moment';
import usePollAnimationFrame from '@/hooks/usePollAnimationFrame';
import useUploadDoorLock, { CardUsage, UploadStatus } from '@/hooks/useUploadDoorLock';
import { fetchRoomInfo, updateRoomCard } from '@/api/card';
import { DATE_TIME_FORMAT } from '@/config';
import { getTimeByUtcOffset } from '@/utils/intl';
import { LockVendorMap } from '../../config';
import {
  CardOperateProcessingStatus,
  emptyAndDataAccuracyStrategyMap,
  ICardInfo,
  isDataNormalUpdateCard,
  MyDoorLockCardClass,
  SingleCardProxyClass,
} from '../helper';
import styles from '../index.module.scss';

const UpdateDoorCardModal = ({ innerRef, enabledVendor, roomId, roomNo, updateCallBack }) => {
  const [open, setOpen] = useState(false);

  const [roomInfo, setRoomInfo] = useState<any>({});
  const [roomInfoLoading, setRoomInfoLoading] = useState(false);

  const [requestStatus, setRequestStatus] = useState<CardOperateProcessingStatus>(
    CardOperateProcessingStatus.Init,
  );

  const usePoll = usePollAnimationFrame();

  // 连接实例
  const instanceRef = useRef<MyDoorLockCardClass>();
  const cardInstance: MyDoorLockCardClass = new SingleCardProxyClass();
  instanceRef.current = cardInstance;

  const { startUploadDoorInfo, endUploadDoorInfo } = useUploadDoorLock();

  // 初始化房间信息
  const initRoomInfo = async () => {
    setRoomInfoLoading(true);
    try {
      const res = await fetchRoomInfo({
        roomId,
      });
      if (res && res.status) {
        setRoomInfo(res.data);
      }
      setRoomInfoLoading(false);
      return res.data;
    } catch (e) {
      setRoomInfoLoading(false);
      setRoomInfo({});
    }
    return {};
  };

  const updateCardInfo = async ({ localCardInfo, tempRoomInfo }) => {
    try {
      const { card_id: cardNo } = localCardInfo;
      const { validityStartTime, validityEndTime } = tempRoomInfo;
      const flag = await isDataNormalUpdateCard(localCardInfo, roomId);
      if (flag) {
        if (instanceRef.current) {
          await instanceRef.current.updateCard({
            roomNo,
            beginTime: validityStartTime
              ? moment(validityStartTime).format('YYYY-MM-DD HH:mm')
              : undefined,
            // 特殊处理，c++预离时间自动加30分钟
            endTime: validityEndTime
              ? moment(validityEndTime).subtract(30, 'minutes').format('YYYY-MM-DD HH:mm')
              : undefined,
            lockType: LockVendorMap[enabledVendor?.lockCode],
          });
          const res: any = await updateRoomCard({
            roomId,
            cardNo,
            oldCardNo: cardNo,
            validityStartTime,
            validityEndTime,
          });
          if (res && res.status) {
            message.success(intl.get('cpm_cards_update_success'));
            if (updateCallBack && typeof updateCallBack === 'function') updateCallBack();
          } else {
            message.error(res.msg || intl.get('cpm_cards_info_update_fail'));
          }
        }
      } else {
        endUploadDoorInfo({
          result: UploadStatus.Fail,
          remark: intl.get('device_door_no_current_room_card'),
        });
      }
    } catch (error) {
      throw new Error(error.msg || '更新卡链路失败');
    }
  };

  const pollUpdateCard = async () => {
    const tempRoomInfo = await initRoomInfo();

    const strategyObj = emptyAndDataAccuracyStrategyMap[enabledVendor?.lockCode];

    setRequestStatus(CardOperateProcessingStatus.Loading);
    usePoll
      .startPoll(
        () => cardInstance.readCard({ lockType: LockVendorMap[enabledVendor?.lockCode] }),
        strategyObj.successFulCodeList,
      )
      .then(
        async (res) => {
          console.log('readData', res);
          const isEmpty = strategyObj.isEmptyCard(res);
          const localCardInfo: ICardInfo = strategyObj.updateCardStatus(res);

          if (!isEmpty) {
            try {
              await updateCardInfo({ localCardInfo, tempRoomInfo });
              setRequestStatus(CardOperateProcessingStatus.RequestSuccess);

              endUploadDoorInfo({ result: UploadStatus.Success });
            } catch (error) {
              setRequestStatus(CardOperateProcessingStatus.RequestError);

              endUploadDoorInfo({ result: UploadStatus.Fail, remark: error.msg });
            }
          } else {
            // 空白卡直接异常
            setRequestStatus(CardOperateProcessingStatus.RequestError);
          }
        },
        (err) => {
          console.log('receive=>>>>error', err.message);
          setRequestStatus(CardOperateProcessingStatus.RequestError);
        },
      )
      .catch(() => {
        setRequestStatus(CardOperateProcessingStatus.RequestError);
      });
  };

  const closeModal = () => {
    setOpen(false);

    usePoll.endPoll();
  };

  const retry = () => {
    startUploadDoorInfo({ type: CardUsage.Update, roomNo });

    setRequestStatus(CardOperateProcessingStatus.Init);

    pollUpdateCard();
  };

  useImperativeHandle(
    innerRef,
    () => ({
      openModal: () => {
        setOpen(true);

        retry();
      },
      closeModal,
    }),
    [],
  );

  const isException = requestStatus === CardOperateProcessingStatus.RequestError;

  return (
    <>
      <Modal
        width={560}
        title={intl.get('cpm_cards_update')}
        open={open}
        maskClosable={false}
        keyboard={false}
        footer={
          <>
            <Space align="start" size={16}>
              {isException && (
                <Button type="primary" onClick={() => retry()}>
                  {intl.get('app_confirm_retry_btn')}
                </Button>
              )}
              <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
            </Space>
          </>
        }
        destroyOnClose
        onCancel={() => closeModal()}
      >
        {isException ? (
          <Alert message={intl.get('cpm_cards_abnormal_canot_update')} type="error" showIcon />
        ) : (
          <Alert message={intl.get('cpm_cards_put_old_on_write_machine')} type="info" showIcon />
        )}

        <Spin spinning={roomInfoLoading}>
          <ul className={styles.cardInfo}>
            <li>
              <label>{intl.get('app_room')}</label>
              <span>{roomInfo.roomNo || '-'}</span>
            </li>
            <li>
              <label>{intl.get('device_lock_number')}</label>
              <span>{roomInfo.lockNo || '-'}</span>
            </li>
            <li>
              <label>{intl.get('manage_license_field_start_label')}</label>
              <span>{getTimeByUtcOffset(roomInfo.validityStartTime, DATE_TIME_FORMAT) || '-'}</span>
            </li>
            <li>
              <label>{intl.get('cpm_cards_end_validity_period')}</label>
              <span>{getTimeByUtcOffset(roomInfo.validityEndTime, DATE_TIME_FORMAT) || '-'}</span>
            </li>
          </ul>
        </Spin>
      </Modal>
    </>
  );
};

export default UpdateDoorCardModal;
